import type { HeroClass } from "./classes.js";

/** Completed-floor records; race records stay private until the race ends.
 *  Deliberately excludes seeds, commands and chat. */
export type RunDoorKind = "key" | "enter" | "guardian";
export interface RunRoom {
  id: number;
  x: number;
  y: number;
  /** Row-major terrain digits (0 floor, 1 wall, 2 pond, 3 table). */
  terrain: string;
  /** -1 = revealed at spawn; null = never revealed. */
  revealedAt: number | null;
}
export interface RunDoor {
  id: number;
  x: number;
  y: number;
  axis: "h" | "v";
  rooms: [number, number];
  kind: RunDoorKind;
  /** -1 = initially open; null = never opened. */
  openedAt: number | null;
  /** Unlock for key/guardian, first physical use for Enter. */
  creditedAt: number | null;
  playerId: number | null;
}
export interface RunPosition {
  tick: number;
  x: number;
  y: number;
  alive: boolean;
  present: boolean;
  disconnected: boolean;
}
export interface RunPartyMember {
  name: string;
  classKind: HeroClass | null;
  level: number | null;
}
export interface RunPlayer extends RunPartyMember {
  id: number;
  ended: "cleared" | "retreated" | "left";
  kills: number;
  deaths: number;
  doors: number;
  gt: number;
  pgt: number;
  positions: RunPosition[];
}
export interface RunTeleport {
  tick: number;
  playerId: number;
  kind: "gt" | "pgt";
}
export interface RunRecord {
  version: 1;
  floorName: string;
  tickMs: number;
  roomTiles: number;
  cols: number;
  rows: number;
  baseRoom: number;
  bossRoom: number;
  rooms: RunRoom[];
  doors: RunDoor[];
  npcCount: number;
  players: RunPlayer[];
  teleports: RunTeleport[];
}
export type RunOutcome = "cleared" | "retreated" | "abandoned" | "unknown";
export type RunEligibility = "ranked" | "dq" | "dnf" | "unrated";
export interface RunSummary {
  /** Race runs link to their verdict and never hold a floor-board rank. */
  raceId?: number;
  id: number;
  floor: number;
  floorName: string;
  size: "small" | "medium" | "large";
  partySize: number;
  ticks: number;
  endedAt: number;
  mapPct: number;
  outcome: RunOutcome;
  eligibility: RunEligibility;
  /** Its own board (party size × size × theme) in the Recent/My streams; the
   *  board's rank in a `view=board` stream, which may include promoted runs. */
  rank: number | null;
  players: RunPartyMember[];
  hasDetail: boolean;
  /** Present only in an authenticated personal history response. */
  personalOutcome?: RunPlayer["ended"];
}
export interface ActiveRunSummary {
  floor: number;
  floorName: string;
  size: "small" | "medium" | "large";
  ticks: number;
  players: RunPartyMember[];
  personalOutcome: "playing" | "left" | "disconnected";
}
export interface RunList {
  runs: RunSummary[];
  active: ActiveRunSummary[];
  total: number;
  /** Publication high-water mark keeps paging stable while new floors end. */
  through: number;
  offset: number;
  limit: number;
  username?: string;
  floors: { id: number; name: string }[];
}

/** One run row on a record-book table. A board is a party-size CUTOFF, not an
 *  exclusive category: the 5-man board holds every clear with at most five
 *  finishers, so a faster four-man clear is promoted into it and keeps its own
 *  #1 on the four-man board. Five players are fastest, so five-player clears
 *  top the widest board — that is what makes it the prestigious one. */
export interface BoardRun {
  id: number;
  rank: number;
  ticks: number;
  endedAt: number;
  partySize: number;
  /** Fewer finishers than the board's cutoff — it ranks here by promotion. */
  promoted: boolean;
  floor: number;
  floorName: string;
  names: string[];
  players: RunPartyMember[];
  hasDetail: boolean;
}
/** One table of the record book: Overall (floor = null, every floor) or one
 *  floor. The footer numbers are the community sheet's: the listed runs'
 *  average, the gap from #1 to the last listed run, and how long #1 has stood. */
export interface RunBoardTable {
  floor: number | null;
  floorName: string;
  /** Top of the board, fastest first. */
  runs: BoardRun[];
  /** Every qualified run in the board — the "See more" count. */
  count: number;
  avgTicks: number | null;
  gapTicks: number | null;
  /** How long the #1 time has stood, in wall-clock seconds. */
  standingSeconds: number | null;
}
/** One view of the record book: a floor size, a party cutoff, and the tables
 *  inside it (Overall first, then one per floor). */
export interface RunBoards {
  size: "small" | "medium" | "large";
  partyMax: number;
  tables: RunBoardTable[];
  /** Floors holding at least one qualified run at each cutoff, index 0 = solo
   *  — the party segment labels' "floors with records / floors". */
  segments: number[];
  floors: { id: number; name: string }[];
}
/** Where one run sits in every board it appears in, from its own party size up
 *  to the maximum. Theme = that floor; Overall = every floor on that board. */
export interface RunBoardRank {
  partyMax: number;
  themeRank: number;
  themeOf: number;
  overallRank: number;
  overallOf: number;
}
export function runEligibility(outcome: RunOutcome, mapPct: number, finishers: number): RunEligibility {
  if (outcome === "unknown") return "unrated";
  if (outcome !== "cleared") return "dnf";
  return mapPct === 100 && finishers > 0 ? "ranked" : "dq";
}
export interface RunDetail {
  raceId?: number;
  /** Race maps remain server-side until the race completes or is voided.
   *  Unavailable covers older mapless records and missing race references. */
  raceMapStatus?: "ongoing" | "available" | "unavailable";
  id: number;
  floor: number;
  floorName: string;
  size: "small" | "medium" | "large";
  partySize: number;
  ticks: number;
  endedAt: number;
  /** The run's own board: party size × size × theme. */
  rank: number | null;
  of: number | null;
  /** Every board it appears in, own party size up to the maximum. Empty for
   *  a run that does not rank (DNF, DQ, older unrated record). */
  ranks: RunBoardRank[];
  outcome: RunOutcome;
  eligibility: RunEligibility;
  mapPct: number;
  names: string[];
  /** Legacy results did not save a map or historical classes. */
  record: RunRecord | null;
}

/** The party-size label a board uses: prestige order, descending. */
export function partyLabel(partySize: number): string {
  return partySize === 1 ? "Solo" : partySize === 2 ? "Duo" : partySize === 3 ? "Trio" : `${partySize} man`;
}

/** Last authoritative sample at or before tick. Binary search makes seeking
 * independent of how long the party spent walking. -1 is the spawn frame. */
export function runPositionAt(positions: readonly RunPosition[], tick: number): RunPosition | null {
  let lo = 0;
  let hi = positions.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (positions[mid]!.tick <= tick) lo = mid + 1;
    else hi = mid;
  }
  return positions[lo - 1] ?? null;
}
